import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { cn } from '@/lib/utils';
import { useAuth, AppRole } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Map,
  Building2,
  Users,
  HardHat,
  Calendar,
  ClipboardCheck,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  BookOpen,
  BarChart3,
  ShieldCheck,
  Eye,
  TrendingUp,
  WifiOff,
  Trash2,
  X,
  Activity,
  Bell,
  Landmark,
  FlaskConical,
  Briefcase,
  AlertTriangle,
  Brain,
  FolderOpen,
  Key,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import SiafoLogo from './SiafoLogo';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  roles?: AppRole[];
  featureFlag?: string; // conditional on feature flag
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  { label: 'Inteligência Geoespacial', href: '/map', icon: Map },
  { label: 'Como Usar', href: '/instructions', icon: BookOpen },
  { label: 'Clusters', href: '/clusters', icon: Building2, roles: ['admin', 'coadmin'] },
  { label: 'Hubs', href: '/hubs', icon: Building2, roles: ['admin', 'coadmin', 'gestor', 'observador'] },
  { label: 'Contratos', href: '/contracts', icon: ClipboardCheck, roles: ['admin', 'coadmin'] },
  { label: 'Obras', href: '/works', icon: HardHat, roles: ['admin', 'coadmin', 'gestor', 'observador'] },
  { label: 'Fiscais de Campo', href: '/professionals', icon: HardHat, roles: ['admin', 'coadmin', 'gestor', 'observador'] },
  { label: 'Agenda', href: '/schedule', icon: Calendar, roles: ['admin', 'coadmin', 'gestor', 'observador'] },
  // Inspeções Mobile removido — rota legada redirecionada
  { label: 'Relatórios', href: '/governanca', icon: ShieldCheck, roles: ['admin', 'coadmin', 'gestor'] },
  { label: 'Indicadores', href: '/indicators', icon: BarChart3, roles: ['admin', 'coadmin', 'gestor'] },
  { label: 'Monitoramento', href: '/monitoramento', icon: TrendingUp, roles: ['admin', 'coadmin', 'gestor'] },
  { label: 'Analytics Risco', href: '/empreendimentos/risk-analytics', icon: Activity, roles: ['admin', 'coadmin', 'admin_cluster', 'gestor_execucao', 'gestor'] },
  { label: 'Central de Alertas', href: '/alertas', icon: Bell, roles: ['admin', 'coadmin', 'gestor'] },
  { label: 'Auditoria Offline', href: '/auditoria-offline', icon: WifiOff, roles: ['admin', 'coadmin', 'gestor'] },
  { label: 'Auditoria', href: '/auditoria', icon: ShieldCheck, roles: ['admin', 'coadmin'] },
  { label: 'Gestão de Pessoas', href: '/pessoas', icon: Users, roles: ['admin', 'coadmin', 'gestor'] },
  { label: 'Empreendimentos', href: '/empreendimentos', icon: Landmark, roles: ['admin', 'coadmin', 'gestor', 'executor'], featureFlag: 'empreendimentos' },
  { label: 'Sandbox de Testes', href: '/empreendimentos/sandbox', icon: FlaskConical, roles: ['admin'], featureFlag: 'empreendimentos' },
  { label: 'QA Dashboard', href: '/empreendimentos/qa-dashboard', icon: FlaskConical, roles: ['admin', 'coadmin'] },
  { label: 'Manual Interno', href: '/manual', icon: BookOpen },
];

// Nav items for IF (Instituição Financeira) financing branch — synced with financiamentoNavConfig
const financiamentoNavItems: NavItem[] = [
  { label: 'Carteira de Contratos', href: '/financiamento/carteira', icon: Briefcase, roles: ['instituicao_financeira', 'admin', 'coadmin'] },
  { label: 'Alertas de Financiamento', href: '/financiamento/alertas', icon: AlertTriangle, roles: ['instituicao_financeira', 'admin', 'coadmin'] },
  { label: 'Inteligência da Carteira', href: '/financiamento/inteligencia', icon: Brain, roles: ['instituicao_financeira', 'admin', 'coadmin'] },
  { label: 'Meus Contratos', href: '/financiamento/meus-contratos', icon: FolderOpen, roles: ['instituicao_financeira', 'admin', 'coadmin'] },
  { label: 'Resgatar Convite', href: '/financiamento/resgatar-convite', icon: Key, roles: ['instituicao_financeira', 'admin', 'coadmin'] },
  { label: 'Administração de Vínculos', href: '/financiamento/admin', icon: Settings, roles: ['admin', 'coadmin'] },
];

interface SidebarProps {
  isMobile?: boolean;
  onCloseMobile?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobile = false, onCloseMobile }) => {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { role, isAdmin, isCoAdmin, isObservador, signOut } = useAuth();
  const { isModuleVisible: empVisible } = useEmpFeatureFlag();

  const visibleNavItems = navItems.filter(item => {
    if (!item.roles) return true;
    if (!role) return false;
    if (!item.roles.includes(role)) return false;
    // Feature flag gating
    if (item.featureFlag === 'empreendimentos' && !empVisible) return false;
    return true;
  });

  const visibleFinanciamentoItems = financiamentoNavItems.filter(item => {
    if (!role) return false;
    return item.roles?.includes(role) ?? false;
  });
  const showFinanciamento = visibleFinanciamentoItems.length > 0;

  const handleLogout = async () => {
    await signOut();
  };

  const handleNavClick = () => {
    if (isMobile && onCloseMobile) {
      onCloseMobile();
    }
  };

  // Em mobile, nunca colapsar - sempre mostrar completo
  const isCollapsed = isMobile ? false : collapsed;

  return (
    <aside
      className={cn(
        'h-screen gradient-sidebar flex flex-col transition-[width] duration-300',
        isMobile ? 'w-[280px] max-w-[85vw]' : 'fixed left-0 top-0 z-50',
        !isMobile && (isCollapsed ? 'w-20' : 'w-64')
      )}
    >
      {/* Header com logo e botão de fechar (mobile) */}
      <div className="px-4 py-4 border-b border-sidebar-border flex items-center justify-between min-h-[64px]">
        <SiafoLogo size="md" showText={!isCollapsed} showSubtitle={!isCollapsed} />
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCloseMobile}
            className="text-sidebar-foreground hover:bg-sidebar-accent min-h-[44px] min-w-[44px] active:scale-95 transition-transform"
            aria-label="Fechar menu"
          >
            <X className="w-6 h-6" />
          </Button>
        )}
      </div>

      {/* Role indicator for observador */}
      {isObservador && !isCollapsed && (
        <div className="mx-3 mt-3 px-3 py-2 bg-sidebar-accent/50 rounded-lg flex items-center gap-2 text-sidebar-foreground/70">
          <Eye className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-medium">Modo Leitura</span>
        </div>
      )}

      {/* Navigation com scroll */}
      <ScrollArea className="flex-1 px-2 py-3">
        <nav className="space-y-1">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={handleNavClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 group',
                  // Área de toque mínima de 44px
                  'min-h-[44px]',
                  // Feedback visual ao toque
                  'active:scale-[0.98] active:bg-sidebar-accent/70',
                  isActive
                    ? 'bg-sidebar-accent text-sidebar-foreground border-l-[3px] border-l-sidebar-ring'
                    : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 border-l-[3px] border-l-transparent'
                )}
              >
                <item.icon className={cn(
                  'w-5 h-5 flex-shrink-0 transition-colors',
                  isActive && 'text-secondary'
                )} />
                {!isCollapsed && (
                  <span className="flex-1 font-medium text-sm truncate">{item.label}</span>
                )}
                {!isCollapsed && item.badge && (
                  <Badge variant="secondary" className="text-xs">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}

          {/* Financiamento branch — IF + admin only */}
          {showFinanciamento && (
            <div className="mt-4 pt-3 border-t border-sidebar-border/50">
              {!isCollapsed && (
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/50">
                  Financiamento de Projetos
                </p>
              )}
              <nav className="space-y-1">
                {visibleFinanciamentoItems.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={handleNavClick}
                      className={cn(
                        'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 group',
                        'min-h-[44px]',
                        'active:scale-[0.98] active:bg-sidebar-accent/70',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-foreground border-l-[3px] border-l-sidebar-ring'
                          : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 border-l-[3px] border-l-transparent'
                      )}
                    >
                      <item.icon className={cn(
                        'w-5 h-5 flex-shrink-0 transition-colors',
                        isActive && 'text-secondary'
                      )} />
                      {!isCollapsed && (
                        <span className="flex-1 font-medium text-sm truncate">{item.label}</span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </div>
          )}
        </nav>
      </ScrollArea>

      {/* Bottom section */}
      <div className="px-2 py-3 border-t border-sidebar-border space-y-1">
        {(isAdmin || isCoAdmin) && (
          <>
            <Link
              to="/data-cleanup"
              onClick={handleNavClick}
              className={cn(
                'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 min-h-[44px]',
                'active:scale-[0.98]',
                location.pathname === '/data-cleanup'
                  ? 'bg-sidebar-accent text-sidebar-foreground'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
              )}
            >
              <Trash2 className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span className="font-medium text-sm">Limpeza de Dados</span>}
            </Link>
            <Link
              to="/settings"
              onClick={handleNavClick}
              className={cn(
                'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 min-h-[44px]',
                'active:scale-[0.98]',
                location.pathname === '/settings'
                  ? 'bg-sidebar-accent text-sidebar-foreground'
                  : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50'
              )}
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span className="font-medium text-sm">Configurações</span>}
            </Link>
          </>
        )}
        <button 
          onClick={() => {
            handleNavClick();
            handleLogout();
          }}
          className={cn(
            'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-150 w-full min-h-[44px]',
            'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50',
            'active:scale-[0.98] active:bg-sidebar-accent/70'
          )}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span className="font-medium text-sm">Sair</span>}
        </button>
      </div>

      {/* Collapse button - only on desktop */}
      {!isMobile && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-20 bg-card border shadow-md hover:bg-muted min-h-[32px] min-w-[32px]"
          onClick={() => setCollapsed(!collapsed)}
          aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-foreground" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-foreground" />
          )}
        </Button>
      )}
    </aside>
  );
};

export default Sidebar;
